import _ from 'lodash'
import { useEffect, useRef, type ReactNode } from 'react'
import { CasinoGameCard } from '@/components/casino/lobby/game-card/CasinoGameCard'
import { CasinoGameCardList } from '@/components/casino/lobby/game-card/CasinoGameCardList'
import { CasinoGameCardSkeleton } from '@/components/casino/lobby/game-card/CasinoGameCardSkeleton'
import { HorizontalCarouselNav } from '@/components/casino/lobby/carousel/HorizontalCarouselNav'
import { useHorizontalCarousel } from '@/hooks/use-horizontal-carousel'
import { CASINO_GAMES_LOAD_MORE_CAROUSEL, CASINO_GAMES_PER_CAROUSEL } from '@/config/casino'
import { cn } from '@/lib/utils'
import type { CasinoGame } from '@/types/casino'

interface CasinoGameSectionProps {
	title: string
	icon?: ReactNode
	games: CasinoGame[]
	leading?: ReactNode
	isLoading?: boolean
	isLoadingMore?: boolean
	isError?: boolean
	hasMoreGames?: boolean
	onLoadMore?: () => void
	onViewAll?: () => void
	viewAllLabel?: string
	emptyMessage?: string
	className?: string
}

export function CasinoGameSection({
	title,
	icon,
	games,
	leading,
	isLoading = false,
	isLoadingMore = false,
	isError = false,
	hasMoreGames = false,
	onLoadMore,
	onViewAll,
	viewAllLabel = 'View all',
	emptyMessage,
	className,
}: CasinoGameSectionProps) {
	const hasCarousel = !isLoading && !isError && (games.length > 0 || Boolean(leading))
	const { scrollRef, canScrollPrev, canScrollNext, handleScroll } = useHorizontalCarousel([leading, games])
	const previousGamesLengthRef = useRef(games.length)
	const canGoNext = !isLoadingMore && (canScrollNext || hasMoreGames)

	const handleNavScroll = (direction: 'prev' | 'next') => {
		if (direction === 'prev') {
			handleScroll('prev')
			return
		}

		if (canScrollNext) {
			handleScroll('next')
			return
		}

		onLoadMore?.()
	}

	useEffect(() => {
		if (previousGamesLengthRef.current > 0 && games.length > previousGamesLengthRef.current && !isLoadingMore && scrollRef.current) {
			scrollRef.current.scrollTo({
				left: scrollRef.current.scrollWidth,
				behavior: 'auto',
			})
		}

		previousGamesLengthRef.current = games.length
	}, [games.length, isLoadingMore, scrollRef])

	return (
		<section className={cn('flex flex-col gap-4', className)}>
			<div className='flex items-center justify-between gap-4'>
				<div className='flex items-center gap-3'>
					{icon}
					<h2 className='text-2xl font-semibold tracking-[-0.12px] text-foreground'>{title}</h2>
				</div>
				{onViewAll || hasCarousel ? (
					<div className='flex items-center gap-3'>
						{onViewAll ? (
							<button
								type='button'
								onClick={onViewAll}
								className='text-sm font-medium tracking-[-0.21px] text-muted-foreground transition-colors hover:text-foreground'
							>
								{viewAllLabel}
							</button>
						) : null}
						{hasCarousel ? <HorizontalCarouselNav canScrollPrev={canScrollPrev} canScrollNext={canGoNext} onScroll={handleNavScroll} /> : null}
					</div>
				) : null}
			</div>

			{isLoading || isError || (games.length === 0 && !leading) ? (
				<CasinoGameCardList
					games={games}
					isLoading={isLoading}
					isError={isError}
					emptyMessage={emptyMessage}
					layout='carousel'
					className='gap-6'
					cardClassName='w-[150px] shrink-0'
					skeletonCount={CASINO_GAMES_PER_CAROUSEL}
				/>
			) : (
				<div className='overflow-visible'>
					<div
						ref={scrollRef}
						className='flex gap-6 overflow-x-auto overflow-y-visible scroll-smooth px-[5px] pb-8 pt-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden'
					>
						{leading}
						{_.map(games, (game) => (
							<CasinoGameCard key={game.id} game={game} className='w-[150px] shrink-0' />
						))}
						{isLoadingMore
							? _.times(CASINO_GAMES_LOAD_MORE_CAROUSEL, (index) => <CasinoGameCardSkeleton key={`casino-game-loading-${index}`} className='w-[150px] shrink-0' />)
							: null}
					</div>
				</div>
			)}
		</section>
	)
}
